import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Alert,
  TabContent,
  TabPane,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { showConfirmationModal } from '../../actions/App';
import { fetchUserProfile } from '../../actions/Auth';

import {
  APIcustomerPortal,
  APIgetPaymentProducts,
  APIpaymentCheckout,
  APIpaymentChangePlan,
  APIpaymentCancel,
} from '../../api/Payments';

import AdminPaymentComplimentary from '../../components/Admin/Payment/Complimentary';
import AdminPaymentPlan from '../../components/Admin/Payment/Plan';
import AdminPaymentPricing from '../../components/Admin/Payment/Pricing';

import { sendTrackingEvent } from '../../helpers';
import {
  subscriptionExpired,
  trialEnded,
} from '../../helpers/App';
import { formatBytes } from '../../helpers/Files';
import { addFlashMessage } from '../../helpers/FlashMessages';

import Loading from '../../components/Loading';

const AdminPayment = ({ dispatch, client }) => {
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [discountMessage, setDiscountMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const intl = useIntl();

  useEffect(() => {
    setLoading(true);
    const params = (new URL(document.location)).searchParams;
    APIgetPaymentProducts(params.get('session_id')).then((response) => {
      if (response.data) {
        setPaymentOptions(response.data.products);
        setDiscountMessage(response.data.discount_message);
        if (params.get('session_id')) {
          if (response.data.products.current) {
            sendTrackingEvent('Payment finished');
          }
        }
      }
      if (params.get('session_id')) {
        window.history.pushState('', '', window.location.pathname);
        window.location.reload();
      }
      setLoading(false);
    });
  }, [dispatch, lastUpdate]);

  const getPrice = (priceId) => {
    let price = null;
    Object.keys(paymentOptions).forEach((po) => {
      if (po !== 'current') {
        paymentOptions[po].prices.forEach((p) => {
          if (p.id === priceId) {
            price = p;
            price.planName = paymentOptions[po].name;
          }
        });
      }
    });
    return price;
  };

  const handleCheckout = (priceId) => {
    setProcessing(true);
    APIpaymentCheckout(priceId).then((response) => {
      if (response.data && response.data.url) {
        window.location = response.data.url;
      }
    });
  };

  const handleManageSubscription = () => {
    setProcessing(true);
    APIcustomerPortal().then((response) => {
      if (response.data && response.data.url) {
        window.location = response.data.url;
      }
    });
  };

  const handleViewInvoices = () => {
    setProcessing(true);
    APIcustomerPortal({ mode: 'invoices' }).then((response) => {
      if (response.data && response.data.url) {
        window.location = response.data.url;
      }
    });
  };

  const handleChangePlan = (priceId) => {
    const price = getPrice(priceId);
    const currentPrice = paymentOptions.current;

    let text = intl.formatMessage(
      {
        defaultMessage: 'You are about to change your current plan ({currentName}, {isCurrentYearPayment, select, yes {annual payments} other {monthly payments}}) to <strong>{newName} {newStorage}</strong>, {isNewYearPayment, select, yes {annual payments} other {monthly payments}}.',
      },
      {
        currentName: currentPrice.name,
        isCurrentYearPayment: currentPrice.interval === 'year' ? 'yes' : 'no',
        isNewYearPayment: price.interval === 'year' ? 'yes' : 'no',
        newName: price.planName,
        newStorage: price.storage_size,
        strong: msg => `<strong>${msg}</strong>`,
      },
    );

    if (currentPrice) {
      if (currentPrice.price < price.price) {
        text = `${text}<br />${intl.formatMessage({ defaultMessage: 'You credit card will be charged immediately.' })}`;
      }
    }

    const modalParams = {
      title: intl.formatMessage({ defaultMessage: 'Plan change' }),
      text,
      confirmText: intl.formatMessage({ defaultMessage: 'Update' }),
      onConfirm: () => (
        APIpaymentChangePlan(priceId).then(() => {
          sendTrackingEvent('Subscription updated');
          setLastUpdate(new Date());
          addFlashMessage({
            type: 'success',
            text: intl.formatMessage({ defaultMessage: 'Your subscription has been updated' }),
          });
          window.location.reload();
        })
      ),
    };
    dispatch(showConfirmationModal(modalParams));
  };

  const handleCancel = () => {
    const freePrice = paymentOptions.free.prices[0];
    const currentPrice = paymentOptions.current;

    const text = intl.formatMessage(
      { defaultMessage: 'Are you sure you want to cancel your Stockpress subscription? {usageAbove, select, yes {{br} <strong>Your current storage usage ({currentUsage}) is above the free limit</strong>. You will not be able to access your files once this action is taken.} other {}}' },
      {
        br: <br />,
        usageAbove: currentPrice.usage > freePrice.storage ? 'yes' : 'no',
        currentUsage: formatBytes(currentPrice.usage),
      },
    );

    const modalParams = {
      title: intl.formatMessage({ defaultMessage: 'Cancel your subscription' }),
      text,
      confirmText: intl.formatMessage({ defaultMessage: 'Cancel your subscription' }),
      onConfirm: () => (
        APIpaymentCancel().then(() => {
          sendTrackingEvent('Subscription canceled');
          dispatch(fetchUserProfile());
          setLastUpdate(new Date());
          addFlashMessage({
            type: 'success',
            text: intl.formatMessage({ defaultMessage: 'Your subscription has been canceled' }),
          });
          window.location.reload();
        })
      ),
    };
    dispatch(showConfirmationModal(modalParams));
  };

  if (client.payment_disabled) {
    return null;
  }

  return (
    <div className="main-content">
      <div className="settings-inner-content">
        <header className="page-header">
          <h3 className="page-title">
            {intl.formatMessage({ defaultMessage: 'Manage Subscription' })}
          </h3>
        </header>
        {!client.has_manual_subscription && (
          <p>
              {intl.formatMessage(
                { defaultMessage: 'Manage or upgrade your subscription.{br}Upgrade at any time and only pay the pro-rated difference in the cost of your new subscription.' },
                { br: <br /> },
              )}
          </p>
        )}
        <div className="settings-payment-wrapper">
          {!loading && (
            <>
              {(trialEnded(client) && !client.is_subscribed) && (
                <Alert color="danger">
                  {intl.formatMessage(
                    { defaultMessage: 'Your free trial has ended. Choose a subscription to continue using Stockpress or get in touch at <a>help@stockpress.co</a>.' },
                    {
                      a: email => <a href="mailto:help@stockpress.co">{email}</a>,
                    },
                  )}
                </Alert>
              )}
              {subscriptionExpired(client) && (
                <Alert color="danger">
                  {intl.formatMessage(
                    { defaultMessage: 'Your subscription expired. Choose a subscription to continue using Stockpress or get in touch at <a>help@stockpress.co</a>.' },
                    {
                      a: email => <a href="mailto:help@stockpress.co">{email}</a>,
                    },
                  )}
                </Alert>
              )}
            </>
          )}
          {(!loading && discountMessage) && (
            <Alert color="success" className="discount-alert">
              <strong>{discountMessage}</strong>
            </Alert>
          )}
          <TabContent className="settings-payment">
            <TabPane className="active">
              {loading ? (
                <Loading />
              ) : (
                <>
                  {paymentOptions.current ? (
                    <AdminPaymentPlan
                      client={client}
                      plan={paymentOptions.current}
                      processing={processing}
                      isManualSubscription={client.has_manual_subscription}
                      handleManageSubscription={handleManageSubscription}
                      handleViewInvoices={handleViewInvoices}
                      handleCancel={handleCancel}
                    />
                  ) : (
                    <>
                      {client.complimentary && (
                        <AdminPaymentComplimentary
                          client={client}
                        />
                      )}
                    </>
                  )}
                  {(!client.complimentary && !client.has_manual_subscription) && (
                    <AdminPaymentPricing
                      paymentOptions={paymentOptions}
                      processing={processing}
                      handleCheckout={handleCheckout}
                      handleChangePlan={handleChangePlan}
                    />
                  )}
                </>
              )}
            </TabPane>
          </TabContent>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  client: state.Auth.profile?.client,
});

AdminPayment.propTypes = {
  dispatch: PropTypes.func.isRequired,
  client: PropTypes.object.isRequired,
};

export default connect(mapStateToProps)(AdminPayment);
